import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../../contexts/ProposalContext";
import { useAuth } from "../../contexts/AuthContext";

// Components
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";
import RfpUploadModal from "./RfpUploadModal";

// Individual Proposal Card Component
const ProposalManagementCard = ({ proposal, onStatusChange, isUpdatingStatus }) => {
  const navigate = useNavigate();

  const statusColors = {
    Active: "bg-emerald-100 text-emerald-700",
    Completed: "bg-blue-100 text-blue-700",
    Archived: "bg-slate-100 text-slate-700",
  };

  const typeColors = {
    RFP: "bg-orange-100 text-orange-800",
    Past_Proposal: "bg-purple-100 text-purple-800",
  };

  const typeLabels = {
    RFP: "Active RFP",
    Past_Proposal: "Past Proposal",
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    if (!value) return "Not specified";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleCardClick = () => {
    navigate(`/proposal-details/${proposal.proposal_id}`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/proposals/edit/${proposal.proposal_id}`);
  };

  const handleManageDocuments = (e) => {
    e.stopPropagation();
    navigate(`/documents/${proposal.proposal_id}`);
  };

  const handleStatusChange = (e) => {
    e.stopPropagation();
    onStatusChange(proposal.proposal_id, e.target.value);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer overflow-hidden"
    >
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex space-x-2">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                typeColors[proposal.proposal_type] || typeColors.RFP
              }`}
            >
              {typeLabels[proposal.proposal_type] || proposal.proposal_type}
            </span>
            {/* Status Dropdown */}
            <select
              value={proposal.status}
              onChange={handleStatusChange}
              disabled={isUpdatingStatus}
              onClick={(e) => e.stopPropagation()}
              className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer transition-opacity ${
                statusColors[proposal.status] || statusColors.Active
              } ${isUpdatingStatus ? "opacity-50 cursor-not-allowed" : "hover:opacity-80"}`}
            >
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Archived">Archived</option>
            </select>
          </div>
          {proposal.due_date && (
            <div className="text-right">
              <div className="text-xs text-slate-500">Due Date</div>
              <div
                className={`text-sm font-medium ${
                  new Date(proposal.due_date) < new Date()
                    ? "text-red-600"
                    : "text-slate-900"
                }`}
              >
                {formatDate(proposal.due_date)}
              </div>
            </div>
          )}
        </div>

        <h3 className="text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
          {proposal.proposal_name}
        </h3>

        {proposal.project_title && (
          <p className="text-sm text-slate-600 mb-2 line-clamp-1">
            {proposal.project_title}
          </p>
        )}

        {proposal.customer_name && (
          <p className="text-sm text-primary-600 font-medium mb-3">
            {proposal.customer_name}
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="px-6 pb-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-lg font-semibold text-slate-900">
              {proposal.document_count || 0}
            </div>
            <div className="text-xs text-slate-600">Documents</div>
          </div>
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-lg font-semibold text-slate-900">
              {proposal.contract_value
                ? formatCurrency(proposal.contract_value)
                : "TBD"}
            </div>
            <div className="text-xs text-slate-600">Value</div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 pb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={handleEdit}
            className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
            title="Edit proposal details"
          >
            <FontAwesomeIcon icon="edit" className="text-xs" />
            <span>Edit</span>
          </button>
          <button
            onClick={handleManageDocuments}
            className="flex-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
            title="Manage documents"
          >
            <FontAwesomeIcon icon="file-alt" className="text-xs" />
            <span>Documents</span>
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Created {formatDate(proposal.created_at)}</span>
          <span>by {proposal.created_by_name}</span>
        </div>
      </div>
    </div>
  );
};

const ProposalList = ({
  filterType = null,
  filterStatus = "Active",
  limit = 20,
  showCreateButton = true,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { proposals, loading, error, fetchProposals, clearError, updateProposalStatus } =
    useProposal();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState(filterType || "");
  const [selectedStatus, setSelectedStatus] = useState(filterStatus || "");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showRfpUploadModal, setShowRfpUploadModal] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      await fetchProposals({
        type: selectedType || undefined,
        status: selectedStatus || undefined,
        customer: searchTerm || undefined,
        limit,
        offset: 0,
      });
    };

    fetchData();
  }, [selectedType, selectedStatus, searchTerm, limit, fetchProposals]);

  const handleProposalClick = (proposalId) => {
    navigate(`/proposal-details/${proposalId}`);
  };

  const handleCreateProposal = () => {
    setShowRfpUploadModal(true);
  };

  const handleStatusChange = async (proposalId, newStatus) => {
    setUpdatingStatusId(proposalId);
    try {
      const result = await updateProposalStatus(proposalId, newStatus);
      if (!result.success) {
        console.error("Failed to update status:", result.error);
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      Active: "bg-emerald-100 text-emerald-800",
      Completed: "bg-blue-100 text-blue-800",
      Archived: "bg-slate-100 text-slate-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          statusColors[status] || statusColors.Active
        }`}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      RFP: "bg-orange-100 text-orange-800",
      Past_Proposal: "bg-purple-100 text-purple-800",
    };

    const typeLabels = {
      RFP: "Active RFP",
      Past_Proposal: "Past Proposal",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          typeColors[type] || typeColors.RFP
        }`}
      >
        {typeLabels[type] || type}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "No date";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    if (!value) return "Not specified";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const filteredAndSortedProposals = proposals
    .filter((proposal) => {
      const matchesSearch =
        !searchTerm ||
        proposal.proposal_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        proposal.customer_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        proposal.project_title
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesSearch;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle date sorting
      if (sortBy === "created_at" || sortBy === "due_date") {
        aValue = new Date(aValue || 0);
        bValue = new Date(bValue || 0);
      }

      // Handle string sorting
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading proposals..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={() => {
          clearError();
          fetchProposals({
            type: selectedType || undefined,
            status: selectedStatus || undefined,
            customer: searchTerm || undefined,
            limit,
            offset: 0,
          });
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Proposals</h2>
            <p className="text-slate-600 mt-1">
              {filteredAndSortedProposals.length} proposal
              {filteredAndSortedProposals.length !== 1 ? "s" : ""} found
            </p>
          </div>

          {showCreateButton && (
            <button
              onClick={handleCreateProposal}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <FontAwesomeIcon icon="plus" />
              <span>Create Proposal</span>
            </button>
          )}
        </div>

        {/* Filters and Search */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <FontAwesomeIcon
              icon="search"
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search proposals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Type Filter */}
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="RFP">Active RFPs</option>
            <option value="Past_Proposal">Past Proposals</option>
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
            <option value="Archived">Archived</option>
          </select>

          {/* Sort */}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split("-");
              setSortBy(field);
              setSortOrder(order);
            }}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="due_date-asc">Due Date (Earliest)</option>
            <option value="due_date-desc">Due Date (Latest)</option>
            <option value="proposal_name-asc">Name (A-Z)</option>
            <option value="proposal_name-desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Proposals Grid */}
      {filteredAndSortedProposals.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
          <FontAwesomeIcon
            icon="file-alt"
            className="text-4xl text-slate-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            No Proposals Found
          </h3>
          <p className="text-slate-600 mb-6">
            {searchTerm || selectedType || selectedStatus
              ? "Try adjusting your filters or search terms."
              : "Get started by creating your first proposal."}
          </p>
          {showCreateButton &&
            !searchTerm &&
            !selectedType &&
            !selectedStatus && (
              <button
                onClick={handleCreateProposal}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 mx-auto"
              >
                <FontAwesomeIcon icon="plus" />
                <span>Create Your First Proposal</span>
              </button>
            )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredAndSortedProposals.map((proposal) => (
            <ProposalManagementCard
              key={proposal.proposal_id}
              proposal={proposal}
              onStatusChange={handleStatusChange}
              isUpdatingStatus={updatingStatusId === proposal.proposal_id}
            />
          ))}
        </div>
      )}

      {/* RFP Upload Modal */}
      <RfpUploadModal
        isOpen={showRfpUploadModal}
        onClose={() => setShowRfpUploadModal(false)}
      />
    </div>
  );
};

export default ProposalList;
